import { type Object3D, Vector4 } from 'three'
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import {
  type CadBatchDashScaleUniforms,
  cloneCadNodeLineMaterial,
  isCadNodeLineMaterial,
} from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { Setting } from '@/plugins/cad/module/common/setting'

/** 批次独占虚线材质，在 GPU 上按每个实体的参考深度保持普通对象的屏幕线型比例。 */
export function createStaticBatchDashMaterial(source: LineMaterial): LineMaterial {
  if (isCadNodeLineMaterial(source)) {
    return cloneCadNodeLineMaterial(source)
  }

  const material = source.clone()
  const compile = source.onBeforeCompile.bind(source)
  const cacheKey = source.customProgramCacheKey.bind(source)
  const perspective = { value: 0 }
  const unitScale = { value: 1 }
  const near = { value: 0.1 }
  material.userData.cadDashUniforms = { perspective, unitScale, near }
  material.onBeforeCompile = (shader, renderer) => {
    compile(shader, renderer)
    shader.uniforms.cadDashPerspective = perspective
    shader.uniforms.cadDashUnitScale = unitScale
    shader.uniforms.cadDashNear = near
    shader.vertexShader = shader.vertexShader
      .replace(
        'uniform float dashScale;',
        `
      uniform float dashScale;
      uniform float cadDashPerspective;
      uniform float cadDashUnitScale;
      uniform float cadDashNear;
      attribute vec3 instanceDashReference;
    `
      )
      .replace(
        'vLineDistance = ( position.y < 0.5 ) ? dashScale * instanceDistanceStart : dashScale * instanceDistanceEnd;',
        `float cadEntityDepth = -(modelViewMatrix * vec4(instanceDashReference, 1.0)).z;
      float cadEntityScale = cadDashPerspective > 0.5 && cadEntityDepth > cadDashNear
        ? cadDashUnitScale / cadEntityDepth : dashScale;
      vLineDistance = cadEntityScale * ((position.y < 0.5) ? instanceDistanceStart : instanceDistanceEnd);`
      )
  }
  material.customProgramCacheKey = () => `${cacheKey()}|cad-entity-dash-v1`
  return material
}

/** 更新各视口共享的投影系数，实体参考点由批次 attribute 提供。 */
export function installStaticBatchDashScale(object: LineSegments2): void {
  const uniforms = object.material.userData.cadDashUniforms as CadBatchDashScaleUniforms | undefined
  if (!uniforms) return
  const renderObject: Object3D = object
  const original = renderObject.onBeforeRender.bind(renderObject)
  const viewport = new Vector4()
  renderObject.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
    original(renderer, scene, camera, geometry, material, group)
    const perspective = (camera as typeof camera & { isPerspectiveCamera?: boolean })
      .isPerspectiveCamera
    uniforms.perspective.value = perspective ? 1 : 0
    if (perspective) {
      renderer.getViewport(viewport)
      uniforms.unitScale.value =
        (viewport.w * Math.abs(camera.projectionMatrix.elements[5]!)) / (2 * Setting.dashSize)
      uniforms.near.value = (camera as typeof camera & { near: number }).near
    }
  }
}
